import { Cropper } from 'react-advanced-cropper'
import 'react-advanced-cropper/dist/style.css'
import { useCallback, useRef, useState } from 'react'
import { endsWith, split } from 'lodash'
import { RefreshIcon, SwitchHorizontalIcon, SwitchVerticalIcon, ZoomInIcon, ZoomOutIcon } from '@heroicons/react/outline'

const dataURLtoFile = (dataUrl, filename) => {
  const arr = split(dataUrl, ',')
  const mime = arr[0].match(/:(.*?);/)[1]
  const bstr = atob(arr[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }
  return new File([u8arr], filename, { type: mime })
}

const ControlButton = ({ onClick, title, children, disabled }) => (
  <button
    type="button"
    title={title}
    disabled={disabled}
    onClick={onClick}
    className="inline-flex items-center justify-center h-9 w-9 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
  >
    {children}
  </button>
)

/**
 * Image cropper with zoom, rotate, flip, pan, and preview-before-confirm.
 * Supports both a modern onConfirm(file, dataUrl) API and the legacy
 * setPreview / setFiles / imageType API.
 */
export const ImageCropper = ({ currentFile, fileName = 'cropped-image.jpg', aspectRatio = 16 / 9, onConfirm, imageType, setPreview, setFiles, close }) => {
  const cropperRef = useRef()
  const [cropping, setCropping] = useState(false)
  const [mode, setMode] = useState('edit')
  const [previewDataUrl, setPreviewDataUrl] = useState(null)
  const [zoom, setZoom] = useState(1)

  const getCropper = () => cropperRef.current

  const handleZoom = useCallback(factor => {
    const cropper = getCropper()
    if (!cropper) return
    cropper.zoomImage(factor)
    setZoom(prev => Math.min(5, Math.max(0.2, +(prev * factor).toFixed(2))))
  }, [])

  const handleZoomSlider = event => {
    const cropper = getCropper()
    if (!cropper) return
    const nextZoom = Number(event.target.value)
    const factor = nextZoom / zoom
    if (factor !== 1) {
      cropper.zoomImage(factor)
      setZoom(nextZoom)
    }
  }

  const handleRotate = degrees => {
    getCropper()?.rotateImage(degrees)
  }

  const handleFlip = (horizontal, vertical) => {
    getCropper()?.flipImage(horizontal, vertical)
  }

  const handleReset = () => {
    getCropper()?.reset()
    setZoom(1)
  }

  const getCroppedDataUrl = () => getCropper()?.getCanvas()?.toDataURL('image/jpeg', 0.92)

  const handleShowPreview = () => {
    const dataUrl = getCroppedDataUrl()
    if (!dataUrl) return
    setPreviewDataUrl(dataUrl)
    setMode('preview')
  }

  const applyCroppedResult = dataUrl => {
    const outputName = imageType || fileName
    const hasImageExt = endsWith(outputName, '.jpg') || endsWith(outputName, '.jpeg') || endsWith(outputName, '.png')
    const createdFile = dataURLtoFile(dataUrl, hasImageExt ? outputName : `${outputName}.jpg`)

    if (onConfirm) {
      onConfirm(createdFile, dataUrl)
      return
    }

    if (setPreview && imageType) {
      setPreview(preview => ({ ...preview, [imageType]: dataUrl }))
    }
    if (setFiles && imageType) {
      setFiles(files => ({ ...files, [imageType]: createdFile }))
    }
    close?.()
  }

  const handleConfirm = () => {
    setCropping(true)
    try {
      const dataUrl = previewDataUrl || getCroppedDataUrl()
      if (!dataUrl) return
      applyCroppedResult(dataUrl)
    } finally {
      setCropping(false)
    }
  }

  return (
    <div className="w-full">
      {mode === 'edit' ? (
        <>
          <Cropper
            ref={cropperRef}
            src={currentFile}
            className="cropper h-[360px] sm:h-[420px] bg-gray-900 rounded-md"
            stencilProps={{
              grid: true,
              aspectRatio,
              movable: true,
              resizable: false,
              handlers: false,
            }}
            rotateImage
            moveImage
            scaleImage
          />

          <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
            <ControlButton title="Zoom out" onClick={() => handleZoom(0.9)}>
              <ZoomOutIcon className="h-5 w-5" />
            </ControlButton>
            <input type="range" min="0.2" max="5" step="0.05" value={zoom} onChange={handleZoomSlider} className="w-28 sm:w-40 accent-dark-600" title="Zoom" />
            <ControlButton title="Zoom in" onClick={() => handleZoom(1.1)}>
              <ZoomInIcon className="h-5 w-5" />
            </ControlButton>
            <ControlButton title="Rotate left" onClick={() => handleRotate(-90)}>
              <RefreshIcon className="h-5 w-5 -scale-x-100" />
            </ControlButton>
            <ControlButton title="Rotate right" onClick={() => handleRotate(90)}>
              <RefreshIcon className="h-5 w-5" />
            </ControlButton>
            <ControlButton title="Flip horizontal" onClick={() => handleFlip(true, false)}>
              <SwitchHorizontalIcon className="h-5 w-5" />
            </ControlButton>
            <ControlButton title="Flip vertical" onClick={() => handleFlip(false, true)}>
              <SwitchVerticalIcon className="h-5 w-5" />
            </ControlButton>
            <ControlButton title="Reset" onClick={handleReset}>
              <span className="text-xs font-medium">Reset</span>
            </ControlButton>
          </div>

          <p className="mt-3 text-center text-xs text-gray-500">Drag to reposition. Zoom and rotate as needed. Crop size is fixed to match the website.</p>

          <div className="flex justify-end mt-4 gap-2">
            <button
              onClick={close}
              type="button"
              className="inline-flex text-red-500 items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md bg-white"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleShowPreview}
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm text-normal rounded-md text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500"
            >
              Preview
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="flex flex-col items-center">
            <p className="mb-3 text-sm text-gray-600">Preview of cropped image</p>
            <div className="relative w-full aspect-video max-h-[360px] bg-gray-100 rounded-md overflow-hidden border border-gray-200 flex items-center justify-center">
              <img src={previewDataUrl} alt="Cropped preview" className="max-h-full max-w-full object-contain" />
            </div>
          </div>

          <div className="flex justify-end mt-4 gap-2">
            <button
              onClick={() => setMode('edit')}
              type="button"
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md bg-white text-gray-700"
            >
              Back to edit
            </button>
            <button
              type="button"
              disabled={cropping}
              onClick={handleConfirm}
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm text-normal rounded-md text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500 disabled:opacity-50"
            >
              {cropping && (
                <svg className="animate-spin h-5 w-5 text-white mr-2" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              )}
              Confirm
            </button>
          </div>
        </>
      )}
    </div>
  )
}

export default ImageCropper
